import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LeadMagnet } from "@/components/LeadMagnet";
import { LeadSection } from "@/components/LeadSection";
import { StickyCta } from "@/components/StickyCta";

const SITE = "https://shopify-freelancer.net";
const TITLE = "Shopify Ratgeber: Conversion, Checkout & Kosten";
const DESCRIPTION =
  "Ratgeber für Shopify-Betreiber: Conversion Optimierung, Checkout, Produktseiten und was Agentur oder Freelancer wirklich kosten, ohne Marketing-Floskeln.";

const articles = [
  {
    to: "/ratgeber/shopify-conversion-optimierung" as const,
    title: "Shopify Conversion Optimierung: der praktische Leitfaden",
    text: "In welcher Reihenfolge Sie die Hebel angehen, von der Messung bis zum A/B-Test.",
  },
  {
    to: "/blog/shopify-checkout-optimieren" as const,
    title: "Shopify Checkout optimieren: Hebel gegen Kaufabbrüche",
    text: "Warum Kunden im letzten Schritt abspringen und was zuverlässig dagegen hilft.",
  },
  {
    to: "/ratgeber/shopify-produktseite-optimieren" as const,
    title: "Shopify Produktseite optimieren: Reihenfolge statt Geschmack",
    text: "Der Aufbau, der Zweifel abbaut, oberhalb des ersten Scrolls beginnt der Umsatz.",
  },
  {
    to: "/ratgeber/shopify-conversion-rate-benchmark" as const,
    title: "Shopify Conversion Rate: Benchmark & Einordnung",
    text: "Was gut ist, was Handlungsbedarf, und wie Sie die Lücke in Euro umrechnen.",
  },
  {
    to: "/ratgeber/shopify-mobile-optimierung" as const,
    title: "Shopify Mobile Optimierung: der Daumen entscheidet",
    text: "Wo mobil der Umsatz liegen bleibt und welche Eingriffe sofort wirken.",
  },
  {
    to: "/ratgeber/shopify-pagespeed-optimieren" as const,
    title: "Shopify Pagespeed optimieren, ohne Theme-Neubau",
    text: "Apps entrümpeln, Bilder und Fonts sauber ausliefern, Effekt sauber messen.",
  },
  {
    to: "/ratgeber/shopify-ab-testing" as const,
    title: "Shopify A/B-Testing: weniger Tests, bessere Entscheidungen",
    text: "Ab wann Testen sich lohnt, wie lange es dauert, und was die Alternative ist.",
  },
  {
    to: "/ratgeber/shopify-agentur-kosten" as const,
    title: "Shopify Agentur Kosten vs. Freelancer",
    text: "Realistische Preisspannen und wann sich welches Modell tatsächlich rechnet.",
  },
  {
    to: "/ratgeber/shopify-b2b-shop-einrichten" as const,
    title: "Shopify B2B Shop einrichten: Preise, Kunden, Bestellwege",
    text: "Kundengruppen, Staffelpreise, Nettopreise und Rechnung, und wo Entwicklung beginnt.",
  },
  {
    to: "/ratgeber/shopify-kosten" as const,
    title: "Shopify Kosten: Pläne, Gebühren, Apps",
    text: "Was der Shop pro Monat wirklich frisst, mit Beispielrechnung für 50.000 € Umsatz.",
  },
  {
    to: "/ratgeber/shopify-shop-erstellen-lassen" as const,
    title: "Shopify Shop erstellen lassen: Kosten & Ablauf",
    text: "Preisspannen nach Umfang, Zeitplan in Wochen und woran Sie ein gutes Angebot erkennen.",
  },
  {
    to: "/blog/shopify-seo-optimierung-guide" as const,
    title: "Shopify SEO: Struktur, Technik, Inhalte",
    text: "Kategorieseiten, Duplicate Content bei Varianten, und Sichtbarkeit in KI-Antworten.",
  },
  {
    to: "/ratgeber/conversion-rate-optimierung" as const,
    title: "Conversion Rate Optimierung: Definition & Ablauf",
    text: "Formel, Ablauf eines CRO-Projekts und was realistisch dabei herauskommt.",
  },
  {
    to: "/ratgeber/shopify-warenkorbabbrueche-reduzieren" as const,
    title: "Warenkorbabbrüche reduzieren",
    text: "Warum 70 % der Warenkörbe leer bleiben und welche Eingriffe sofort wirken.",
  },
  {
    to: "/ratgeber/shopify-experte" as const,
    title: "Shopify Experte finden: Auswahl & Prüffragen",
    text: "Drei Profile hinter dem Begriff, realistische Preise und fünf Fragen vor der Beauftragung.",
  },
  {
    to: "/ratgeber/shopify-entwickler" as const,
    title: "Shopify Entwickler: Aufgaben & Kosten",
    text: "Welche Theme-Arbeiten sich rechnen, wann eine App ersetzt gehört, mit Preisrahmen.",
  },
  {
    to: "/ratgeber/shopify-vs-woocommerce" as const,
    title: "Shopify vs. WooCommerce",
    text: "Gesamtkosten, Wartung und Wirkung auf die Kaufabschlussrate im ehrlichen Vergleich.",
  },
  {
    to: "/ratgeber/shopify-migration" as const,
    title: "Shopify Migration ohne Sichtbarkeitsverlust",
    text: "Zeitplan, Kosten und der Weiterleitungsplan, an dem die meisten Umzüge scheitern.",
  },
  {
    to: "/ratgeber/shopware-zu-shopify" as const,
    title: "Shopware zu Shopify wechseln",
    text: "Eigenschaften, Rule Builder und Kundengruppen sauber übersetzen, mit Kostenrahmen.",
  },
  {
    to: "/ratgeber/magento-zu-shopify" as const,
    title: "Magento zu Shopify migrieren",
    text: "Attributsets, Store Views und eigene Module: wo der Aufwand wirklich entsteht.",
  },
  {
    to: "/ratgeber/jtl-zu-shopify" as const,
    title: "JTL-Shop zu Shopify",
    text: "Wawi behalten, Storefront tauschen: Connector, Stücklisten und Weiterleitungen.",
  },
  {
    to: "/ratgeber/gambio-zu-shopify" as const,
    title: "Gambio zu Shopify umziehen",
    text: "Datenübernahme, Rechtstexte, Versandregeln und alte Adressen mit Rankings.",
  },
  {
    to: "/ratgeber/prestashop-zu-shopify" as const,
    title: "PrestaShop zu Shopify wechseln",
    text: "Attribute, Merkmale und Module sauber übersetzen, mit Kostenrahmen und Zeitplan.",
  },
  {
    to: "/ratgeber/oxid-zu-shopify" as const,
    title: "OXID eShop zu Shopify",
    text: "Kundengruppen, Staffelpreise und Warenwirtschaft: wo der Aufwand wirklich entsteht.",
  },
  {
    to: "/ratgeber/wix-zu-shopify" as const,
    title: "Wix zu Shopify wechseln",
    text: "Wann sich der Umzug lohnt, was exportierbar ist und was er realistisch kostet.",
  },
  {
    to: "/ratgeber/etsy-zu-shopify" as const,
    title: "Etsy zu Shopify: eigener Shop statt Marktplatz",
    text: "Gebühren richtig rechnen, Kunden übernehmen und Reichweite selbst aufbauen.",
  },
  {
    to: "/ratgeber/shopify-headless" as const,
    title: "Shopify Headless & Hydrogen",
    text: "Wann sich die Architektur lohnt und wann ein optimiertes Theme klüger ist.",
  },
  {
    to: "/ratgeber/shopify-liquid" as const,
    title: "Shopify Liquid: Anpassung statt App",
    text: "Was direkt im Theme lösbar ist und wo die Grenze zur Entwicklung liegt.",
  },
  {
    to: "/ratgeber/shopify-metafields" as const,
    title: "Shopify Metafelder richtig nutzen",
    text: "Produktdaten strukturieren, filterbar machen und sauber im Theme ausgeben.",
  },
  {
    to: "/ratgeber/shopify-bundles" as const,
    title: "Shopify Bundles erstellen",
    text: "Sets, die den Warenkorb erhöhen: mit App, ohne App und sauber gemessen.",
  },
  {
    to: "/ratgeber/shopify-subscriptions" as const,
    title: "Shopify Subscriptions: Abo-Verkauf",
    text: "Wiederkehrender Umsatz: Apps, Zahlungsarten und die Gestaltung des Angebots.",
  },
  {
    to: "/ratgeber/shopify-schnittstellen" as const,
    title: "Shopify Schnittstellen anbinden",
    text: "Billbee, JTL, ERP und wann eine individuelle Schnittstelle nötig wird.",
  },
  {
    to: "/ratgeber/shopify-datev-schnittstelle" as const,
    title: "Shopify DATEV Schnittstelle",
    text: "Belegexport, Gutschriften und Sammelbuchungen sauber einrichten.",
  },
  {
    to: "/ratgeber/shopify-probleme" as const,
    title: "Shopify Probleme lösen",
    text: "Die häufigsten Fehlerquellen und die Reihenfolge, in der Sie eingrenzen.",
  },
  {
    to: "/ratgeber/shopify-oder-wix" as const,
    title: "Shopify oder Wix",
    text: "Ab welcher Sortimentsgröße welches System das passende ist.",
  },
  {
    to: "/ratgeber/shopify-oder-wordpress" as const,
    title: "Shopify oder WordPress",
    text: "Die Systemfrage ist im Kern eine Frage nach dem Betrieb.",
  },
  {
    to: "/ratgeber/shopify-impressum" as const,
    title: "Shopify Impressum einrichten",
    text: "Seite anlegen, im Footer und Checkout verlinken, erreichbar halten.",
  },
  {
    to: "/ratgeber/shopify-datenschutzerklaerung" as const,
    title: "Shopify Datenschutzerklärung",
    text: "Einbinden, Consent sauber koppeln und bei neuen Apps nachziehen.",
  },
  {
    to: "/ratgeber/ecommerce-freelancer" as const,
    title: "E-Commerce Freelancer beauftragen",
    text: "Profile, Preismodelle und fünf Fragen vor der Beauftragung.",
  },
];



export const Route = createFileRoute("/ratgeber/")({
  component: RatgeberPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/ratgeber` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/ratgeber` }],
  }),
});

function RatgeberPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        <section className="veil border-b border-border/60 py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h1 className="max-w-3xl text-4xl font-bold leading-[1.08] md:text-5xl">
              Shopify Ratgeber: das, was ich sonst im{" "}
              <span className="text-signal">Projekt</span> erkläre
            </h1>
            <p className="mt-5 max-w-xl text-muted-foreground">
              Keine Listen mit 50 generischen Tipps, sondern die Reihenfolge und die Zahlen,
              mit denen ich in echten Shops arbeite.
            </p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground">
              Damit Sie schneller erkennen, was Ihrem Shop gerade im Weg steht, was Sie selbst
              lösen können und wann Unterstützung sinnvoll ist.
            </p>
          </div>
        </section>

        <section className="py-14">
          <div className="mx-auto grid max-w-6xl gap-5 px-5 md:grid-cols-2">
            {articles.map((a) => (
              <Link
                key={a.to}
                to={a.to}
                className="shadow-lift group rounded-lg border border-border bg-card p-6 transition-transform hover:-translate-y-0.5"
              >
                <h2 className="text-lg font-semibold md:text-xl">{a.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a.text}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                  Lesen <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className="pb-14">
          <div className="mx-auto max-w-3xl px-5">
            <LeadMagnet source="ratgeber-hub-checkliste" />
          </div>
        </section>

        <LeadSection source="ratgeber-hub" />
      </main>
      <StickyCta />
      <SiteFooter />
    </div>
  );
}
